import { VERSION_NAME_MAX_LENGTH } from './versionNames';
import { IConfigIssue, IRoutingConfigMetadataBody } from '../model/types';

export const VERSION_COMMENT_MAX_LENGTH = 512;

export interface IVersionMetadataInput {
  name: string;
  // Sent as null when empty: the backend keeps the old comment for ''.
  comment: string;
}

export interface IVersionMetadataErrors {
  name?: string;
  comment?: string;
}

const NAME_REQUIRED_MESSAGE = 'Name is required.';

// Shared by Save Draft and Edit details; checked trimmed, like the backend.
export const validateVersionMetadata = ({
  name,
  comment,
}: IVersionMetadataInput): IVersionMetadataErrors => {
  const errors: IVersionMetadataErrors = {};
  const trimmedName = name.trim();

  if (!trimmedName) errors.name = NAME_REQUIRED_MESSAGE;
  else if (trimmedName.length > VERSION_NAME_MAX_LENGTH) {
    errors.name = `Name must be at most ${VERSION_NAME_MAX_LENGTH} characters (it has ${trimmedName.length}).`;
  }
  const commentLength = comment.trim().length;
  if (commentLength > VERSION_COMMENT_MAX_LENGTH) {
    errors.comment = `Comment must be at most ${VERSION_COMMENT_MAX_LENGTH} characters (it has ${commentLength}).`;
  }

  return errors;
};

export const hasVersionMetadataErrors = (errors: IVersionMetadataErrors): boolean =>
  Boolean(errors.name || errors.comment);

// errors.name / errors.comment go under their inputs, the rest on the form.
export const splitMetadataIssues = (
  issues: IConfigIssue[],
): { fields: IVersionMetadataErrors; general: string | null } => {
  const fields: IVersionMetadataErrors = {};
  const general: string[] = [];

  issues.forEach(issue => {
    if (issue.path === 'name' && !fields.name) fields.name = issue.message;
    else if (issue.path === 'comment' && !fields.comment) fields.comment = issue.message;
    else general.push(issue.message);
  });

  return { fields, general: general.length ? general.join(' ') : null };
};

// Only the given fields: the PATCH keeps names unique, so an unchanged name is never re-sent.
export const toMetadataBody = ({
  name,
  comment,
}: Partial<IVersionMetadataInput>): IRoutingConfigMetadataBody => ({
  ...(name !== undefined ? { name: name.trim() } : {}),
  ...(comment !== undefined ? { comment: comment.trim() || null } : {}),
});
